/* eslint-disable react-hooks/refs */

"use client";

import { Identifier } from "@/types/domain";
import { setupListeners } from "@reduxjs/toolkit/query";
import { Persistor, persistStore } from "redux-persist";
import { PersistGate } from "redux-persist/integration/react";
import React, { PropsWithChildren, useEffect, useRef, useState } from "react";
import { Provider } from "react-redux";
import { getFromCookie } from "@lib/cookies";
import { AppStore } from "@lib/redux/types";
import { APP_NAME_DOMAIN } from "@features/application/app-consts";
import { setDefaultWorkspaceId } from "@features/application/app-slice";
import { AuthSessionController } from "@features/auth/controllers/auth-session-controller";
import { DEFAULT_WORKSPACE_ID_COOKIE_KEY } from "@features/workspaces/workspaces-consts";
import { makeApplicationStore } from "./store";

/**
 * StoreProvider is a React component that provides the Redux store to its children.
 * It initializes the store and renders bootstrap controllers when available.
 *
 * @param {Object} props - The component props.
 * @param {ReactNode} props.children - The child components that will have access to the Redux store.
 * @returns The Provider component wrapping the children.
 */
export const StoreProvider = ({
  children
}: PropsWithChildren) => {
  const storeRef = useRef<AppStore>(undefined);
  const persistorRef = useRef<Persistor>(undefined);
  // const unsubscribeCallback = useRef<() => void>(undefined);

  // DEPS
  // TODO Temporary off by https://github.com/uidotdev/usehooks/issues/361
  // const { level, charging, supported } = useBattery();

  const [defaultWorkspaceIdFromCookie, setDefaultWorkspaceIdFromCookie] = useState<Identifier | undefined>();

  // Initialize the store on the first render
  if (!storeRef.current) {
    // Create the store instance the first time this renders
    storeRef.current = makeApplicationStore();
    persistorRef.current = persistStore(storeRef.current);
  }
  const store = storeRef.current;
  useEffect(() => {
    console.log(`[${APP_NAME_DOMAIN}] turn on refetchOnFocus/refetchOnReconnect behaviors`);
    const detachListeners = setupListeners(store.dispatch);
    return () => {
      console.log(`[${APP_NAME_DOMAIN}] turn off refetchOnFocus/refetchOnReconnect behaviors`);
      detachListeners();
    };
  }, [store]);
  useEffect(() => {
    // Get default workspace ID from cookies
    getFromCookie(DEFAULT_WORKSPACE_ID_COOKIE_KEY).then(defaultWorkspaceId => {
      if (defaultWorkspaceId) {
        setDefaultWorkspaceIdFromCookie(defaultWorkspaceId);
      }
    });
  }, []);
  useEffect(() => {
    // If workspace ID exists, dispatch it to store
    if (defaultWorkspaceIdFromCookie) {
      store.dispatch(setDefaultWorkspaceId(defaultWorkspaceIdFromCookie));
    }
  }, [defaultWorkspaceIdFromCookie, store]);

  // TODO Temporary off by https://github.com/uidotdev/usehooks/issues/361
  // useEffect(() => {
  //   const triggerToOn = !supported || (level && level >= 0.25) || charging;
  //   const triggerToOff = level && level < 0.25 && !charging;
  //
  //   if (storeRef.current && !unsubscribeCallback.current && triggerToOn) {
  //     // optional, but required for refetchOnFocus/refetchOnReconnect behaviors
  //     // see `setupListeners` docs - takes an optional callback as the 2nd arg for customization
  //     console.warn(`[${APP_NAME_DOMAIN}] turn on refetchOnFocus/refetchOnReconnect behaviors`);
  //     unsubscribeCallback.current = setupListeners(storeRef.current.dispatch);
  //   }
  //   if (storeRef.current && unsubscribeCallback.current && triggerToOff) {
  //     console.warn(`[${APP_NAME_DOMAIN}] turn off refetchOnFocus/refetchOnReconnect behaviors`);
  //     unsubscribeCallback.current();
  //     unsubscribeCallback.current = undefined;
  //   }
  // }, [level, charging, supported]);

  // Provide the Redux store with child components
  return <Provider store={store}>
      <AuthSessionController store={store} />
      <PersistGate loading={null} persistor={persistorRef.current as Persistor}>
        {children}
      </PersistGate>
    </Provider>;
};